/**
 * RettVerk 1.1 - Encryption Service (Privacy-First v2.0)
 * Full klient-side kryptering med AES-256-GCM
 * Sist oppdatert: 20. februar 2026
 * 
 * SIKKERHETSPRINSIPPER:
 * - Hovednøkkel beskyttes med brukerpassord via PBKDF2
 * - Alle dokumenter krypteres med AES-256-GCM
 * - Random IV per krypteringsoperasjon
 * - Ingen nøkler forlater enheten
 * - Ingen serveravhengighet
 */

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════

const DB_NAME = 'RettVerkEncryption';
const DB_VERSION = 1;
const STORES = {
  MASTER_KEY: 'masterKey',
  SETTINGS: 'encryptionSettings'
};

// PBKDF2 parameters (høy iterasjonstall for sikkerhet)
const PBKDF2_ITERATIONS = 600000;
const PBKDF2_HASH = 'SHA-256';
const SALT_LENGTH = 32;
const IV_LENGTH = 12; // 96 bits for AES-GCM

// Encryption algorithm
const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;

let dbInstance = null;
let cachedMasterKey = null;
let keyExpiryTimeout = null;

// ═══════════════════════════════════════════════════════════════
// ENGINE STATE MANAGEMENT (v1.2.0 Hotfix)
// ═══════════════════════════════════════════════════════════════

const ENGINE_STATES = {
  UNINITIALIZED: 'uninitialized',
  INITIALIZING: 'initializing',
  READY: 'ready',
  ERROR: 'error'
};

let engineState = ENGINE_STATES.UNINITIALIZED;
let initializationPromise = null;

// ═══════════════════════════════════════════════════════════════
// DATABASE INITIALIZATION
// ═══════════════════════════════════════════════════════════════

/**
 * Initialize the encryption database
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    engineState = ENGINE_STATES.INITIALIZING;
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      engineState = ENGINE_STATES.ERROR;
      console.warn('[Encryption] Database initialization failed');
      reject(request.error);
    };
    
    request.onsuccess = () => {
      dbInstance = request.result;
      engineState = ENGINE_STATES.READY;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      if (!db.objectStoreNames.contains(STORES.MASTER_KEY)) {
        db.createObjectStore(STORES.MASTER_KEY, { keyPath: 'id' });
      }
      
      if (!db.objectStoreNames.contains(STORES.SETTINGS)) {
        db.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
      }
    };
  });
};

/**
 * Ensure encryption engine is ready (v1.2.0 Hotfix)
 * @param {number} maxWaitMs - Maximum wait time in milliseconds
 * @returns {Promise<boolean>} True if engine is ready
 */
export const ensureEngineReady = async (maxWaitMs = 2000) => {
  const POLL_INTERVAL = 50;
  const maxAttempts = Math.ceil(maxWaitMs / POLL_INTERVAL);
  
  // If already ready, return immediately
  if (engineState === ENGINE_STATES.READY && dbInstance) {
    return true;
  }
  
  // If currently initializing, wait for it
  if (initializationPromise) {
    try {
      await initializationPromise;
      return engineState === ENGINE_STATES.READY;
    } catch {
      // Continue to retry below
    }
  }
  
  // Poll for ready state
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (engineState === ENGINE_STATES.READY && dbInstance) {
      return true;
    }
    
    // Try to initialize if not already doing so
    if (engineState === ENGINE_STATES.UNINITIALIZED || engineState === ENGINE_STATES.ERROR) {
      try {
        initializationPromise = initDB();
        await initializationPromise;
        initializationPromise = null;
        if (engineState === ENGINE_STATES.READY) {
          return true;
        }
      } catch {
        initializationPromise = null;
        // Continue polling
      }
    }
    
    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
  }
  
  // After max wait, try one reinitialize
  console.warn('[Encryption] Engine not ready after ' + maxWaitMs + 'ms, attempting reinitialize');
  
  // Force reset and reinitialize
  dbInstance = null;
  engineState = ENGINE_STATES.UNINITIALIZED;
  
  try {
    initializationPromise = initDB();
    await initializationPromise;
    initializationPromise = null;
    return engineState === ENGINE_STATES.READY;
  } catch (err) {
    console.warn('[Encryption] Reinitialize failed:', err.message);
    initializationPromise = null;
    return false;
  }
};

/**
 * Get current engine state (v1.2.0 Hotfix)
 */
export const getEngineState = () => engineState;

// ═══════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Convert ArrayBuffer to Base64
 */
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/**
 * Convert Base64 to ArrayBuffer
 */
const base64ToArrayBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * Generate cryptographically secure random bytes
 */
const generateRandomBytes = (length) => {
  return crypto.getRandomValues(new Uint8Array(length));
};

/**
 * Generate a secure hash of content (for integrity verification)
 */
export const generateContentHash = async (content) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return arrayBufferToBase64(hashBuffer);
};

// ═══════════════════════════════════════════════════════════════
// KEY DERIVATION (PBKDF2)
// ═══════════════════════════════════════════════════════════════

/**
 * Derive a key from password using PBKDF2
 * @param {string} password - User's password
 * @param {Uint8Array} salt - Random salt
 * @returns {Promise<CryptoKey>} Derived key
 */
export const deriveKeyFromPassword = async (password, salt) => {
  const encoder = new TextEncoder();
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: PBKDF2_ITERATIONS,
      hash: PBKDF2_HASH
    },
    passwordKey,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
};

// ═══════════════════════════════════════════════════════════════
// MASTER KEY MANAGEMENT
// ═══════════════════════════════════════════════════════════════

/**
 * Generate a new master key
 * @returns {Promise<CryptoKey>} New master key
 */
export const generateMasterKey = async () => {
  return crypto.subtle.generateKey(
    { name: ALGORITHM, length: KEY_LENGTH },
    true, // extractable for wrapping
    ['encrypt', 'decrypt']
  );
};

/**
 * Encrypt and store the master key with password protection
 * @param {string} password - User's password
 * @returns {Promise<boolean>} Success status
 */
export const setupEncryption = async (password) => {
  try {
    const db = await initDB();
    
    // Generate new master key
    const masterKey = await generateMasterKey();
    
    // Generate salt for password derivation
    const salt = generateRandomBytes(SALT_LENGTH);
    
    // Derive wrapping key from password
    const wrappingKey = await deriveKeyFromPassword(password, salt);
    
    // Export master key
    const exportedMasterKey = await crypto.subtle.exportKey('raw', masterKey);
    
    // Generate IV for wrapping
    const wrapIv = generateRandomBytes(IV_LENGTH);
    
    // Encrypt (wrap) the master key
    const encryptedMasterKey = await crypto.subtle.encrypt(
      { name: ALGORITHM, iv: wrapIv },
      wrappingKey,
      exportedMasterKey
    );
    
    // Store encrypted master key with salt and IV
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.MASTER_KEY, STORES.SETTINGS], 'readwrite');
      const keyStore = transaction.objectStore(STORES.MASTER_KEY);
      const settingsStore = transaction.objectStore(STORES.SETTINGS);
      
      const keyData = {
        id: 'master_key',
        encryptedKey: arrayBufferToBase64(encryptedMasterKey),
        salt: arrayBufferToBase64(salt),
        iv: arrayBufferToBase64(wrapIv),
        algorithm: ALGORITHM,
        iterations: PBKDF2_ITERATIONS,
        created_at: new Date().toISOString()
      };
      
      keyStore.put(keyData);
      settingsStore.put({ key: 'encryption_enabled', value: true });
      settingsStore.put({ key: 'encryption_setup_at', value: new Date().toISOString() });
      
      transaction.oncomplete = () => {
        // Cache the master key for immediate use
        cachedMasterKey = masterKey;
        setKeyExpiry();
        resolve(true);
      };
      
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Failed to setup encryption:', error);
    throw new Error('Kunne ikke opprette kryptering: ' + error.message);
  }
};

/**
 * Unlock the master key with password
 * @param {string} password - User's password
 * @returns {Promise<CryptoKey>} Decrypted master key
 */
export const unlockMasterKey = async (password) => {
  try {
    const db = await initDB();
    
    return new Promise(async (resolve, reject) => {
      const transaction = db.transaction([STORES.MASTER_KEY], 'readonly');
      const store = transaction.objectStore(STORES.MASTER_KEY);
      const request = store.get('master_key');
      
      request.onsuccess = async () => {
        if (!request.result) {
          reject(new Error('Ingen krypteringsnøkkel funnet. Kjør oppsett først.'));
          return;
        }
        
        try {
          const { encryptedKey, salt, iv } = request.result;
          
          // Derive key from password
          const wrappingKey = await deriveKeyFromPassword(
            password, 
            new Uint8Array(base64ToArrayBuffer(salt))
          );
          
          // Decrypt master key
          const decryptedKeyData = await crypto.subtle.decrypt(
            { name: ALGORITHM, iv: new Uint8Array(base64ToArrayBuffer(iv)) },
            wrappingKey,
            base64ToArrayBuffer(encryptedKey)
          );
          
          // Import as CryptoKey
          const masterKey = await crypto.subtle.importKey(
            'raw',
            decryptedKeyData,
            { name: ALGORITHM },
            false,
            ['encrypt', 'decrypt']
          );
          
          // Cache the key
          cachedMasterKey = masterKey;
          setKeyExpiry();
          
          resolve(masterKey);
        } catch (err) {
          reject(new Error('Feil passord eller korrupt nøkkel'));
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Set key expiry timer (lock after 30 minutes of inactivity)
 */
const setKeyExpiry = () => {
  if (keyExpiryTimeout) {
    clearTimeout(keyExpiryTimeout);
  }
  
  keyExpiryTimeout = setTimeout(() => {
    cachedMasterKey = null;
  }, 30 * 60 * 1000); // 30 minutes
};

/**
 * Refresh key expiry (call on user activity)
 */
export const refreshKeyExpiry = () => {
  if (cachedMasterKey) {
    setKeyExpiry();
  }
};

/**
 * Check if master key is unlocked
 */
export const isKeyUnlocked = () => {
  return cachedMasterKey !== null;
};

/**
 * Lock the master key (clear from memory)
 */
export const lockMasterKey = () => {
  cachedMasterKey = null;
  if (keyExpiryTimeout) {
    clearTimeout(keyExpiryTimeout);
    keyExpiryTimeout = null;
  }
};

/**
 * Get the cached master key (throws if not unlocked)
 */
const getMasterKey = async () => {
  if (!cachedMasterKey) {
    throw new Error('Krypteringsnøkkel er låst. Lås opp med passord først.');
  }
  refreshKeyExpiry();
  return cachedMasterKey;
};

// ═══════════════════════════════════════════════════════════════
// ENCRYPTION/DECRYPTION OPERATIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Encrypt text content
 * @param {string} text - Plain text to encrypt
 * @returns {Promise<string>} Base64-encoded encrypted data (IV + ciphertext)
 */
export const encryptText = async (text) => {
  if (!text) return null;
  
  const key = await getMasterKey();
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const iv = generateRandomBytes(IV_LENGTH);
  
  const encrypted = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    key,
    data
  );
  
  // Combine IV + ciphertext
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  
  return arrayBufferToBase64(combined.buffer);
};

/**
 * Decrypt text content
 * @param {string} encryptedBase64 - Base64-encoded encrypted data
 * @returns {Promise<string>} Decrypted plain text
 */
export const decryptText = async (encryptedBase64) => {
  if (!encryptedBase64) return null;
  
  const key = await getMasterKey();
  const combined = new Uint8Array(base64ToArrayBuffer(encryptedBase64));
  
  // Extract IV and ciphertext
  const iv = combined.slice(0, IV_LENGTH);
  const ciphertext = combined.slice(IV_LENGTH);
  
  const decrypted = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv },
    key,
    ciphertext
  );
  
  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
};

/**
 * Encrypt a file/blob
 * @param {Blob} file - File to encrypt
 * @returns {Promise<{encrypted: Blob, metadata: object}>} Encrypted blob with metadata
 */
export const encryptFile = async (file) => {
  const key = await getMasterKey();
  const iv = generateRandomBytes(IV_LENGTH);
  
  // Read file as ArrayBuffer
  const fileData = await file.arrayBuffer();
  
  // Encrypt
  const encrypted = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    key,
    fileData
  );
  
  // Create encrypted blob with IV prepended
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  
  return {
    encrypted: new Blob([combined], { type: 'application/octet-stream' }),
    metadata: {
      originalName: file.name,
      originalType: file.type,
      originalSize: file.size,
      encryptedAt: new Date().toISOString(),
      algorithm: ALGORITHM
    }
  };
};

/**
 * Decrypt a file/blob
 * @param {Blob} encryptedBlob - Encrypted blob
 * @param {object} metadata - File metadata
 * @returns {Promise<Blob>} Decrypted file
 */
export const decryptFile = async (encryptedBlob, metadata) => {
  const key = await getMasterKey();
  
  // Read encrypted data
  const encryptedData = await encryptedBlob.arrayBuffer();
  const combined = new Uint8Array(encryptedData);
  
  // Extract IV and ciphertext
  const iv = combined.slice(0, IV_LENGTH);
  const ciphertext = combined.slice(IV_LENGTH);
  
  // Decrypt
  const decrypted = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv },
    key,
    ciphertext
  );
  
  return new Blob([decrypted], { type: metadata?.originalType || 'application/octet-stream' });
};

/**
 * Encrypt JSON data
 * @param {object} data - Object to encrypt
 * @returns {Promise<string>} Encrypted base64 string
 */
export const encryptJSON = async (data) => {
  return encryptText(JSON.stringify(data));
};

/**
 * Decrypt JSON data
 * @param {string} encryptedBase64 - Encrypted base64 string
 * @returns {Promise<object>} Decrypted object
 */
export const decryptJSON = async (encryptedBase64) => {
  const text = await decryptText(encryptedBase64);
  return JSON.parse(text);
};

// ═══════════════════════════════════════════════════════════════
// ENCRYPTION STATUS & SETTINGS
// ═══════════════════════════════════════════════════════════════

/**
 * Check if encryption is set up
 */
export const isEncryptionSetup = async () => {
  try {
    const db = await initDB();
    
    return new Promise((resolve) => {
      const transaction = db.transaction([STORES.MASTER_KEY], 'readonly');
      const store = transaction.objectStore(STORES.MASTER_KEY);
      const request = store.get('master_key');
      
      request.onsuccess = () => {
        resolve(!!request.result);
      };
      
      request.onerror = () => resolve(false);
    });
  } catch {
    return false;
  }
};

/**
 * Get encryption status
 */
export const getEncryptionStatus = async () => {
  const isSetup = await isEncryptionSetup();
  const isUnlocked = isKeyUnlocked();
  
  return {
    isSetup,
    isUnlocked,
    algorithm: ALGORITHM,
    keyLength: KEY_LENGTH,
    pbkdf2Iterations: PBKDF2_ITERATIONS
  };
};

/**
 * Change encryption password
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 */
export const changePassword = async (currentPassword, newPassword) => {
  // First unlock with current password to verify
  const masterKey = await unlockMasterKey(currentPassword);
  
  const db = await initDB();
  
  // Generate new salt
  const newSalt = generateRandomBytes(SALT_LENGTH);
  
  // Derive new wrapping key
  const newWrappingKey = await deriveKeyFromPassword(newPassword, newSalt);
  
  // Export master key (since we have it unlocked, we need to make it extractable)
  // This requires re-generating if not extractable, but we cached it
  const exportedMasterKey = await crypto.subtle.exportKey('raw', masterKey);
  
  // Generate new IV
  const newIv = generateRandomBytes(IV_LENGTH);
  
  // Re-encrypt master key
  const encryptedMasterKey = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv: newIv },
    newWrappingKey,
    exportedMasterKey
  );
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.MASTER_KEY], 'readwrite');
    const store = transaction.objectStore(STORES.MASTER_KEY);
    
    const keyData = {
      id: 'master_key',
      encryptedKey: arrayBufferToBase64(encryptedMasterKey),
      salt: arrayBufferToBase64(newSalt),
      iv: arrayBufferToBase64(newIv),
      algorithm: ALGORITHM,
      iterations: PBKDF2_ITERATIONS,
      updated_at: new Date().toISOString()
    };
    
    store.put(keyData);
    
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Verify password without unlocking
 */
export const verifyPassword = async (password) => {
  try {
    await unlockMasterKey(password);
    return true;
  } catch {
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════
// EXPORT FOR TESTING
// ═══════════════════════════════════════════════════════════════

export default {
  // Setup
  setupEncryption,
  isEncryptionSetup,
  getEncryptionStatus,
  
  // Engine state (v1.2.0 Hotfix)
  ensureEngineReady,
  getEngineState,
  
  // Key management
  generateMasterKey,
  deriveKeyFromPassword,
  unlockMasterKey,
  lockMasterKey,
  isKeyUnlocked,
  refreshKeyExpiry,
  changePassword,
  verifyPassword,
  
  // Encryption operations
  encryptText,
  decryptText,
  encryptFile,
  decryptFile,
  encryptJSON,
  decryptJSON,
  
  // Utilities
  generateContentHash
};
